<template>
  <div class="one-screen-analytic-messages">
    <SpinnerLoading
      v-if="showLoader"
      class="loader_center"
      color="blue"
    />

    <template v-else-if="messages.length > 0">
      <component
        :is="analyticMessageViewComponents[message.analyticType]"
        v-for="(message, messageIndex) in messages"
        :key="messageIndex"
        :analytic-message="message"
        class="one-screen-analytic-messages__analytic-message"
        @play-event-start="$emit('play-event-start', $event)"
        @show-full-screenshot="$emit('show-full-screenshot', $event)"
      />
    </template>

    <template v-else>
      <br>
      <p>{{ $t('noEvents') }}</p>
    </template>
  </div>
</template>

<script>
import {CameraInfo} from "@/store/cameras/index.js";
import {ACTION_LOAD_ARCHIVE_EVENTS_BY_CAMERA} from "@/store/analytics/index.js";
import {analyticMessageViewMixin} from "@/components/events/mixins.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import {parkingDetectionEventsMixin} from "@/components/oneScreen/parkingDetectionEventsMixin.js";
const ARCHIVE_MESSAGES_RELOAD_DELAY_MS = 300;
const LOADING_SPINNER_DELAY_MS = 300;
/**
 * Компонент для отображения списка архивных событий (сообщений).
 * Применяется для отображения событий аналитики камер за определенный период времени и для перехода ко времени наступления этих событий.
 */
export default {
  name: "ArchiveEvents",
  mixins: [
    analyticMessageViewMixin,
    parkingDetectionEventsMixin,
  ],
  props: {
    /**
     * Экземпляр {@link CameraInfo} по которому отображается информация в компоненте.
     */
    cameraInfo: {
      type: CameraInfo,
      required: true
    },
    /**
     * Начало отрезка времени для запроса архивных событий.
     */
    archiveFrom: {
      type: Date,
      default: null,
    },
    /**
     * Конец отрезка времени для запроса архивных событий.
     */
    archiveTo: {
      type: Date,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      messages: [],
      messagesRequestCounter: 0,
      messagesRefreshTimeoutId: null,
      loadingTimeoutId: null,
    };
  },
  computed: {
    /**
     * Показывает спиннер только для основного запроса или первичной загрузки parking_detection.
     *
     * @return {Boolean}
     */
    showLoader() {
      return this.isLoading || (this.hasParkingDetection() && this.isParkingStateLoading && this.messages.length === 0);
    },
  },
  watch: {
    /**
     * Отслеживание изменений начала отрезка времени для запроса архива событий.
     *
     * @param {Date} newValue
     * @param {Date} oldValue
     */
    archiveFrom(newValue, oldValue) {
      if (!this.liveParkingMode && +newValue !== +oldValue) {
        this.scheduleLoadMessages();
      }
    },
    /**
     * Отслеживание изменений конца отрезка времени для запроса архива событий.
     *
     * @param {Date} newValue
     * @param {Date} oldValue
     */
    archiveTo(newValue, oldValue) {
      if (!this.liveParkingMode && +newValue !== +oldValue) {
        this.scheduleLoadMessages();
      }
    },
    "cameraInfo.number"(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      if (!this.liveParkingMode) {
        this.scheduleLoadMessages();
      }
    },
  },
  /**
   * Запрос архивных событий на начальном заданном отрезке времени.
   */
  mounted() {
    if (this.liveParkingMode) {
      this.reloadLiveParkingState();
      return;
    }
    this.loadMessages();
  },
  beforeDestroy() {
    clearTimeout(this.messagesRefreshTimeoutId);
    clearTimeout(this.loadingTimeoutId);
  },
  methods: {
    /**
     * Показывает спиннер только если основной запрос длится заметно дольше обычного.
     *
     * @param {Number} requestNumber
     */
    scheduleLoading(requestNumber) {
      clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = setTimeout(() => {
        if (requestNumber === this.messagesRequestCounter) {
          this.isLoading = true;
        }
      }, LOADING_SPINNER_DELAY_MS);
    },
    /**
     * Скрывает отложенный спиннер основного запроса.
     *
     * @param {Number} requestNumber
     */
    stopLoading(requestNumber) {
      if (requestNumber !== this.messagesRequestCounter) {
        return;
      }
      clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = null;
      this.isLoading = false;
    },
    /**
     * Откладывает загрузку событий, чтобы быстрые перемотки архива не плодили лишние запросы.
     */
    scheduleLoadMessages() {
      clearTimeout(this.messagesRefreshTimeoutId);
      this.messagesRefreshTimeoutId = setTimeout(
        () => this.loadMessages(),
        ARCHIVE_MESSAGES_RELOAD_DELAY_MS
      );
    },
    /**
     * Запрашивает архивные события по камере на выбранном диапазоне времени
     * и складывает их в this.messages.
     *
     * Особый кейс: события парковки (parking_detection).
     * Состояние парковки всегда приходит из events/last/.
     * archive_events используются только для остальных событий и зарубок на таймлайне.
     * Также эмитим актуальные parkingSpots наверх для плеера.
     */
    async loadMessages() {
      clearTimeout(this.messagesRefreshTimeoutId);
      const requestNumber = ++this.messagesRequestCounter;
      this.scheduleLoading(requestNumber);

      try {
        const shouldLoadParkingState = this.hasParkingDetection(),
              [messages, parkingMessage] = await Promise.all([
                this.$store.dispatch(
                  `analytics/${ACTION_LOAD_ARCHIVE_EVENTS_BY_CAMERA}`, {
                    cameraNumber: this.cameraInfo.number,
                    archiveFrom: this.archiveFrom,
                    archiveTo: this.archiveTo,
                  }
                ),
                shouldLoadParkingState
                  ? this.loadLastParkingEvent({emitSpots: false})
                  : Promise.resolve(null),
              ]);

        if (requestNumber !== this.messagesRequestCounter) {
          return;
        }
        messages.forEach((analyticMessage) => {
          analyticMessage.cameraInfo = this.cameraInfo;
        });
        const otherMessages = messages.filter(
          (message) => message.analyticType !== ANALYTICS.parking_detection
        );
        if (!shouldLoadParkingState) {
          this.messages = messages;
          return;
        }
        const parkingMessageForDisplay = parkingMessage || this.lastParkingMessage;
        if (parkingMessageForDisplay) {
          parkingMessageForDisplay.cameraInfo = this.cameraInfo;
          if (parkingMessage) {
            this.emitParkingSpots(parkingMessageForDisplay);
          }
        }
        this.messages = [...otherMessages, parkingMessageForDisplay].filter(Boolean).sort(
          (a, b) => +b.date - +a.date
        );
      } finally {
        this.stopLoading(requestNumber);
      }
    },
  }
};
</script>
